import React from 'react'
import { DownloadOutlined } from '@ant-design/icons'
import { Button, Modal, Popover, Table, Tag, Typography } from 'antd'
import { ColumnProps } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { parseDate } from '../../../../../../config/dayjsConfig'
import { Delivery, DeliveryStatus } from '../../../../../../types/deliverySubscription/Delivery'
import { DeliverySubscription } from '../../../../../../types/deliverySubscription/DeliverySubscription'
import { useDeliveries } from '../../../../../../features/deliverySubscriptions/queries/useDeliveries'
import deliveryRequest from '../../../../../../services/api/deliveryRequest'
import { showErrorMessage } from '../../../../../../utils/message'
import { autoDownload } from '../../../../../../utils/helpers'

interface DeliveriesProps {
  deliverySubscription?: DeliverySubscription
  setDeliverySubscription: (deliverySubscription?: DeliverySubscription) => void
  setDeliveryModalVisibility: (visible: boolean) => void
  visible: boolean
}

const Deliveries: React.FC<DeliveriesProps> = ({
  deliverySubscription,
  setDeliverySubscription,
  setDeliveryModalVisibility,
  visible
}) => {
  const { t } = useTranslation()
  const { data: deliveries = [], isLoading } = useDeliveries(deliverySubscription?.contractId, deliverySubscription?.id)

  const download = async (uuid: string) => {
    try {
      const response = await deliveryRequest<BlobPart>({
        method: 'GET',
        url: `/deliveries/${uuid}/download`,
        responseType: 'blob'
      })
      autoDownload(`${uuid}.pdf`, response)
    } catch {
      showErrorMessage()
    }
  }

  const handleClose = () => {
    setDeliverySubscription(undefined)
    setDeliveryModalVisibility(false)
  }

  const columns: ColumnProps<Delivery>[] = [
    {
      title: t('status:status'),
      dataIndex: 'status',
      key: 'status',
      render: (text: string) => {
        const tr = (s: string) => t(`status:${s.toLowerCase()}`)
        switch (text) {
          case DeliveryStatus.FAILED:
            return <Tag color="red">{tr(text)}</Tag>
          case DeliveryStatus.FINISHED:
            return <Tag color="green">{tr(text)}</Tag>
          default:
            return <Tag>{tr(text)}</Tag>
        }
      }
    },
    {
      title: t('subscriptionsPage:deliveryEndTime'),
      dataIndex: 'endTime',
      key: 'endTime',
      render: (text: string) => parseDate(text)?.format('lll')
    },
    {
      title: t('subscriptionsPage:reported-month'),
      key: 'date',
      render: (delivery: Delivery) => {
        if (delivery.meta?.periodGroups) {
          const { startDate } = delivery.meta.periodGroups[0]
          return parseDate(startDate)?.format('MM/YYYY')
        }
        return undefined
      }
    },
    {
      title: t('error:error'),
      dataIndex: 'errors',
      key: 'errors',
      ellipsis: true,
      render: (text: string) =>
        text && (
          <Popover
            content={
              <Typography.Text code copyable>
                {text}
              </Typography.Text>
            }
          >
            <Typography.Text code>{text}</Typography.Text>
          </Popover>
        )
    },
    {
      title: t('global:actions'),
      key: 'actions',
      align: 'right',
      render: ({ uuid }: Delivery) =>
        uuid && (
          <Button icon={<DownloadOutlined />} type="link" onClick={() => download(uuid)}>
            {t('global:download')}
          </Button>
        )
    }
  ]

  return (
    <Modal
      title={t('subscriptionsPage:deliveries')}
      open={visible}
      onOk={handleClose}
      onCancel={handleClose}
      afterClose={handleClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      maskClosable={false}
      width="50%"
    >
      <Table
        loading={isLoading}
        rowKey={record => record.id.toString()}
        pagination={{ pageSize: 10 }}
        size="small"
        dataSource={[...deliveries].sort(
          (a, b) => (parseDate(b?.endTime)?.valueOf() || 0) - (parseDate(a?.endTime)?.valueOf() || 0)
        )}
        columns={columns}
      />
    </Modal>
  )
}

export default Deliveries
